import { Outlet, createFileRoute } from '@tanstack/react-router';

import { SecureLinkGate } from '../auth/secure-link';
import {
  NotificationsClientProvider,
  notificationsRuntime,
} from '../lib/api/notifications-client-context';
import { PortalClientProvider, portalRuntime } from '../lib/api/portal-client-context';
import { useAppNavigate } from '../lib/session/navigate';
import { RequireAuth } from '../lib/session/require-auth';
import { PortalNavProvider } from '../portal/components';

/**
 * `/account` — the portal's layout route (M10.4, M10.5).
 *
 * Everything under `/account` is one guarded subtree, which is the point of putting the
 * guard here rather than on each screen: a portal route added next year is protected
 * because it is in this folder, not because someone remembered. `RequireAuth` is still a
 * *rendering* guard and never an authorization one — every `/api/v1/patient/*` endpoint
 * enforces its own access on the server (rules §17.2, §22.2, PATIENT_AUTH_UI.md §3.6).
 *
 * ## The secure-link gate sits above the guard, on purpose
 *
 * An emailed portal link lands on the page it is about, carrying its credential in the
 * fragment (`auth/secure-link.tsx`, M10.2) — so the token has to be spent *before*
 * anything decides whether this browser has a session. Below the guard, a patient
 * following a reminder from their inbox would be bounced to `/login?next=` holding a
 * perfectly good link they never got to use.
 *
 * The layout also supplies the two things the portal screens cannot have of their own.
 * **Navigation:** `PortalNavProvider` hands them the router's navigate, so `PortalLink`
 * renders a real `<a href>` that does an SPA transition — and, without a provider (a test,
 * a static render), the same link is just a link. **The portal client** (M10.1): a screen
 * rendered without `PortalClientProvider` reaches nobody, so "does this page talk to the
 * patient API?" is a property of the subtree that mounts it rather than of a component,
 * exactly as the payments, intake and notification runtimes are installed on a shell
 * (`lib/api/portal-client-context.tsx`).
 *
 * M2.9's landing page is no longer mounted here; its identity card and sign-out now live
 * on `/account/profile`. See PATIENT_PORTAL_UI.md §7.
 */
export const Route = createFileRoute('/account')({
  component: AccountLayout,
});

function AccountLayout() {
  const navigate = useAppNavigate();

  return (
    <SecureLinkGate
      onSignedIn={(path) => {
        navigate(path, { replace: true });
      }}
      onNavigate={navigate}
    >
      <RequireAuth>
        <PortalNavProvider navigate={navigate}>
          <PortalClientProvider client={portalRuntime}>
            <NotificationsClientProvider client={notificationsRuntime}>
              <Outlet />
            </NotificationsClientProvider>
          </PortalClientProvider>
        </PortalNavProvider>
      </RequireAuth>
    </SecureLinkGate>
  );
}
